package com.ijoin

import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.ijoin.getPlatform
import com.ijoin.data.mapper.toRequest
import com.ijoin.data.mapper.toWizardData
import com.ijoin.data.model.ChatThread
import com.ijoin.data.repository.EventRepository
import com.ijoin.deeplink.DeepLink
import com.ijoin.deeplink.DeepLinkHandler
import com.ijoin.i18n.LocaleManager
import com.ijoin.i18n.ProvideLocaleManager
import com.ijoin.i18n.strings
import com.ijoin.platform.BackHandler
import com.ijoin.platform.MobileOS
import com.ijoin.platform.getWebMobileOS
import com.ijoin.platform.openUrl
import com.ijoin.platform.shortcut.ProfileShortcutLauncher
import com.ijoin.push.WebPushInitializer
import com.ijoin.ui.components.AppStoreBanner
import com.ijoin.ui.design.eventColorScheme
import com.ijoin.ui.screens.BillingScreen
import com.ijoin.ui.screens.ChatListScreen
import com.ijoin.ui.screens.ChatScreen
import com.ijoin.ui.screens.CreateEventWizardScreen
import com.ijoin.ui.screens.EventCardScreen
import com.ijoin.ui.screens.EventScreenWrapper
import com.ijoin.ui.screens.EventDetailsScreen
import com.ijoin.ui.screens.FollowingListScreen
import com.ijoin.ui.screens.HomeScreen
import com.ijoin.ui.payment.PaymentFlowArgs
import com.ijoin.ui.screens.EventWizardData
import com.ijoin.ui.screens.PaymentScreen
import com.ijoin.ui.screens.PasswordResetScreen
import com.ijoin.ui.screens.ProfileScreen
import com.ijoin.ui.screens.TicketScreen
import com.ijoin.ui.screens.TicketsScreen
import com.ijoin.ui.screens.UserCardScreen
import com.ijoin.ui.tickets.TicketsViewModel
import com.ijoin.ui.components.LegalAcceptanceModal
import com.ijoin.ui.components.UpdateDialog
import com.ijoin.update.AppUpdateChecker
import com.ijoin.update.openStoreUrl
import com.ijoin.data.model.UpdateType
import com.ijoin.data.repository.LegalRepository
import com.ijoin.data.storage.TokenManager
import com.ijoin.ui.design.EventCardAccentDark
import com.ijoin.ui.design.EventPrimaryButton
import com.ijoin.ui.design.EventSurfaceCard
import com.ijoin.ui.design.EventTextAction
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.launch
import org.jetbrains.compose.ui.tooling.preview.Preview

@Composable
@Preview
fun App() {
    val localeManager = remember { LocaleManager() }
    val authRepository = remember { com.ijoin.data.repository.AuthRepository() }
    var isAuthenticated by remember { mutableStateOf(authRepository.isAuthenticated()) }
    val isWebPlatform = remember { getPlatform().isWeb }
    var showWebPushPrompt by remember {
        mutableStateOf(isWebPlatform && WebPushInitializer.shouldShowPermissionPrompt())
    }

    // App store banner for mobile web users (shows on every visit)
    val webMobileOS = remember { getWebMobileOS() }
    var showAppStoreBanner by remember {
        mutableStateOf(isWebPlatform && webMobileOS != MobileOS.NONE && isAuthenticated)
    }
    var appStoreBannerDismissed by remember { mutableStateOf(false) }

    // App update check for mobile platforms
    val updateResult by AppUpdateChecker.updateState.collectAsState()
    val isMobilePlatform = remember { getPlatform().isMobile }

    // Check for updates on app start (mobile only)
    LaunchedEffect(Unit) {
        if (isMobilePlatform) {
            AppUpdateChecker.checkForUpdate()
        }
    }

    // Set up token expiration callback
    // Use a mutable state to track token expiration and trigger recomposition
    var tokenExpired by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        com.ijoin.data.api.ApiClient.onTokenExpired = {
            // Set flag to trigger recomposition
            tokenExpired = true
        }
    }

    // React to token expiration
    LaunchedEffect(tokenExpired) {
        if (tokenExpired) {
            // Update authentication status
            isAuthenticated = false
            tokenExpired = false // Reset flag
        }
    }

    // Legal documents acceptance check
    val legalRepository = remember { LegalRepository() }
    var pendingLegalDocuments by remember { mutableStateOf<List<String>>(emptyList()) }
    var legalCheckDone by remember { mutableStateOf(false) }

    // Check legal status when authenticated
    LaunchedEffect(isAuthenticated) {
        if (isAuthenticated) {
            legalRepository.getAcceptanceStatus()
                .onSuccess { status ->
                    pendingLegalDocuments = status.pendingDocuments
                    legalCheckDone = true
                }
                .onFailure {
                    // If check fails, don't block the user
                    pendingLegalDocuments = emptyList()
                    legalCheckDone = true
                }
        } else {
            pendingLegalDocuments = emptyList()
            legalCheckDone = false
        }
    }

    LaunchedEffect(isAuthenticated) {
        if (!isAuthenticated) {
            showAppStoreBanner = false
            appStoreBannerDismissed = false
        } else if (!appStoreBannerDismissed && isWebPlatform && webMobileOS != MobileOS.NONE) {
            showAppStoreBanner = true
        }
    }

    ProvideLocaleManager(localeManager) {
        MaterialTheme(colorScheme = eventColorScheme()) {
            Column(modifier = Modifier.fillMaxSize()) {
                // App store banner for mobile web users (at the top)
                if (showAppStoreBanner) {
                    AppStoreBanner(
                        mobileOS = webMobileOS,
                        onDismiss = {
                            appStoreBannerDismissed = true
                            showAppStoreBanner = false
                        }
                    )
                }

                // App update dialog for mobile platforms
                updateResult?.let { result ->
                    if (result.updateType != UpdateType.NONE) {
                        UpdateDialog(
                            updateResult = result,
                            onUpdate = { openStoreUrl(result.updateUrl) },
                            onDismiss = { AppUpdateChecker.dismissUpdate() }
                        )
                    }
                }

                // Legal documents acceptance modal (blocks app until accepted)
                if (isAuthenticated && legalCheckDone && pendingLegalDocuments.isNotEmpty()) {
                    LegalAcceptanceModal(
                        pendingDocuments = pendingLegalDocuments,
                        onAccepted = {
                            pendingLegalDocuments = emptyList()
                        },
                        legalRepository = legalRepository
                    )
                }

                Box(modifier = Modifier.fillMaxSize().weight(1f)) {
                    AppContent(
                        isAuthenticated = isAuthenticated,
                        onAuthSuccess = { isAuthenticated = true },
                        onLogout = {
                            isAuthenticated = false
                            showAppStoreBanner = false
                        }
                    )

                    if (isWebPlatform && showWebPushPrompt) {
                        WebPushPermissionPrompt(
                            modifier = Modifier
                                .align(Alignment.BottomCenter)
                                .padding(16.dp),
                            onEnableClick = {
                                WebPushInitializer.requestPermissionFromUserGesture { _ ->
                                    showWebPushPrompt = WebPushInitializer.shouldShowPermissionPrompt()
                                }
                            },
                            onDismissClick = { showWebPushPrompt = false }
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun WebPushPermissionPrompt(
    modifier: Modifier = Modifier,
    onEnableClick: () -> Unit,
    onDismissClick: () -> Unit
) {
    val strings = strings()
    EventSurfaceCard(modifier = modifier) {
        Text(
            text = strings.enableNotificationsTitle,
            style = MaterialTheme.typography.titleMedium,
            color = EventCardAccentDark
        )
        Text(
            text = strings.enableNotificationsBody,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.End,
            verticalAlignment = Alignment.CenterVertically
        ) {
            EventTextAction(
                text = strings.notNowAction,
                onClick = onDismissClick
            )
            Spacer(modifier = Modifier.width(8.dp))
            EventPrimaryButton(
                text = strings.allowNotificationsAction,
                onClick = onEnableClick,
                modifier = Modifier.height(40.dp)
            )
        }
    }
}

private enum class ChatEntryPoint {
    HOME,
    LIST,
    EVENT
}

private enum class AuthScreen {
    LOGIN,
    REGISTER,
    RESET
}

@Composable
private fun AuthenticationFlow(
    onAuthSuccess: () -> Unit,
    initialScreen: AuthScreen = AuthScreen.LOGIN
) {
    var currentScreen by remember { mutableStateOf(initialScreen) }
    var pendingResetToken by remember { mutableStateOf<String?>(null) }

    val handlePasswordResetLink: (DeepLink.PasswordReset) -> Unit = { resetLink ->
        pendingResetToken = resetLink.token
        currentScreen = AuthScreen.RESET
    }

    val initialDeepLink = remember {
        when (val pending = DeepLinkHandler.peekPendingDeepLink()) {
            is DeepLink.PasswordReset -> {
                DeepLinkHandler.consumePendingDeepLink()
                pending
            }
            else -> null
        }
    }

    LaunchedEffect(initialDeepLink) {
        initialDeepLink?.let(handlePasswordResetLink)
    }

    LaunchedEffect(Unit) {
        DeepLinkHandler.deepLinks.collect { deepLink ->
            if (deepLink is DeepLink.PasswordReset) {
                handlePasswordResetLink(deepLink)
            }
        }
    }

    BackHandler(enabled = currentScreen != AuthScreen.LOGIN) {
        when (currentScreen) {
            AuthScreen.REGISTER -> currentScreen = AuthScreen.LOGIN
            AuthScreen.RESET -> {
                currentScreen = AuthScreen.LOGIN
                pendingResetToken = null
            }
            else -> Unit
        }
    }

    when (currentScreen) {
        AuthScreen.LOGIN -> com.ijoin.ui.screens.LoginScreen(
            onLoginSuccess = onAuthSuccess,
            onNavigateToRegister = { currentScreen = AuthScreen.REGISTER },
            onNavigateToPasswordReset = {
                pendingResetToken = null
                currentScreen = AuthScreen.RESET
            }
        )
        AuthScreen.REGISTER -> com.ijoin.ui.screens.RegisterScreen(
            onRegisterSuccess = onAuthSuccess,
            onNavigateToLogin = { currentScreen = AuthScreen.LOGIN }
        )
        AuthScreen.RESET -> PasswordResetScreen(
            initialToken = pendingResetToken,
            onNavigateBack = {
                currentScreen = AuthScreen.LOGIN
                pendingResetToken = null
            }
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun AppContent(
    isAuthenticated: Boolean,
    onAuthSuccess: () -> Unit,
    onLogout: () -> Unit
) {
    var currentScreen by remember { mutableStateOf("home") }
    var authReturnScreen by remember { mutableStateOf("home") }
    var authCancelScreen by remember { mutableStateOf("home") }
    var authStartScreen by remember { mutableStateOf(AuthScreen.LOGIN) }
    var pendingOwnProfileAfterAuth by remember { mutableStateOf(false) }
    var pendingProfileOrigin by remember { mutableStateOf("home") }
    var selectedEventId by remember { mutableStateOf<String?>(null) }
    var selectedTicketId by remember { mutableStateOf<String?>(null) }
    var ticketReturnScreen by remember { mutableStateOf("home") }
    var ticketReturnEventId by remember { mutableStateOf<String?>(null) }
    var paymentArgs by remember { mutableStateOf<PaymentFlowArgs?>(null) }
    var selectedUserId by remember { mutableStateOf<String?>(null) }
    var userCardReturnScreen by remember { mutableStateOf("home") }
    val userCardHistory = remember { mutableStateListOf<String>() }
    var eventCardReturnScreen by remember { mutableStateOf("home") }
    var profileReturnScreen by remember { mutableStateOf("home") }
    var billingUserId by remember { mutableStateOf<String?>(null) }
    var billingReturnScreen by remember { mutableStateOf("user_card") }
    var followingListUserId by remember { mutableStateOf<String?>(null) }
    var followingListReturnScreen by remember { mutableStateOf("user_card") }
    var selectedChatThread by remember { mutableStateOf<ChatThread?>(null) }
    var pendingChatUserId by remember { mutableStateOf<String?>(null) }
    var pendingChatUserName by remember { mutableStateOf<String?>(null) }
    var chatEntryPoint by remember { mutableStateOf(ChatEntryPoint.HOME) }
    val strings = strings()

    val scope = rememberCoroutineScope()
    val eventRepository = remember { EventRepository() }
    val authRepository = remember { com.ijoin.data.repository.AuthRepository() }
    var isSavingWizard by remember { mutableStateOf(false) }
    var wizardErrorMessage by remember { mutableStateOf<String?>(null) }
    var wizardEditingEventId by remember { mutableStateOf<String?>(null) }
    var wizardInitialData by remember { mutableStateOf<EventWizardData?>(null) }
    var wizardReturnScreen by remember { mutableStateOf("home") }
    var userCurrency by remember { mutableStateOf<String?>(null) }

    // Fetch user currency when authenticated
    LaunchedEffect(isAuthenticated) {
        if (isAuthenticated) {
            authRepository.getCurrentUser()
                .onSuccess { user ->
                    userCurrency = user.currency
                }
        } else {
            userCurrency = null
        }
    }

    fun isGuestAllowed(screen: String): Boolean {
        return screen == "home" ||
            screen == "event_card" ||
            screen == "event_details" ||
            screen == "user_card" ||
            screen == "auth"
    }

    fun requestAuth(
        targetScreen: String,
        cancelScreen: String = currentScreen,
        preserveProfilePending: Boolean = false,
        startScreen: AuthScreen = AuthScreen.LOGIN
    ) {
        if (!preserveProfilePending) {
            pendingOwnProfileAfterAuth = false
        }
        authStartScreen = startScreen
        authReturnScreen = targetScreen
        authCancelScreen = cancelScreen
        currentScreen = "auth"
    }

    fun runIfAuthenticated(
        targetScreen: String,
        cancelScreen: String = currentScreen,
        action: () -> Unit
    ) {
        if (isAuthenticated) {
            action()
        } else {
            requestAuth(targetScreen, cancelScreen)
        }
    }

    fun setTicketNavigation(
        ticketId: String,
        returnScreen: String,
        returnEventId: String? = null
    ) {
        selectedTicketId = ticketId
        ticketReturnScreen = returnScreen
        ticketReturnEventId = returnEventId
    }

    fun handleGoogleAuthExchange(code: String) {
        scope.launch {
            authRepository.exchangeGoogle(code)
                .onSuccess {
                    onAuthSuccess()

                    // Record pending legal acceptance from OAuth flow
                    val tokenManager = TokenManager()
                    val pendingVersions = tokenManager.getPendingLegalAcceptance()
                    if (pendingVersions != null && pendingVersions.isNotEmpty()) {
                        val legalRepository = LegalRepository()
                        // Accept registration documents (Terms, Privacy, Refund)
                        legalRepository.acceptRegistrationDocuments()
                        tokenManager.clearPendingLegalAcceptance()
                    }

                    val target = if (authReturnScreen.isBlank() || authReturnScreen == "auth") {
                        "home"
                    } else {
                        authReturnScreen
                    }
                    currentScreen = target
                }
                .onFailure {
                    authStartScreen = AuthScreen.LOGIN
                    authReturnScreen = "home"
                    authCancelScreen = "home"
                    currentScreen = "auth"
                }
        }
    }

    LaunchedEffect(Unit) {
        ProfileShortcutLauncher.events.collect { shortcut ->
            userCardHistory.clear()
            selectedUserId = shortcut.userId
            userCardReturnScreen = "home"
            currentScreen = "user_card"
            ProfileShortcutLauncher.clearPending()
        }
    }

    LaunchedEffect(isAuthenticated) {
        if (!isAuthenticated && !isGuestAllowed(currentScreen)) {
            requestAuth(
                targetScreen = currentScreen,
                cancelScreen = "home",
                startScreen = AuthScreen.LOGIN
            )
        }
    }

    // Handle deep links
    LaunchedEffect(Unit) {
        // First, check for pending deep link (from app launch)
        when (val pending = DeepLinkHandler.peekPendingDeepLink()) {
            is DeepLink.PasswordReset -> {
                authStartScreen = AuthScreen.LOGIN
                authReturnScreen = "home"
                authCancelScreen = if (isGuestAllowed(currentScreen)) currentScreen else "home"
                currentScreen = "auth"
            }
            is DeepLink.GoogleAuthExchange -> {
                DeepLinkHandler.consumePendingDeepLink()
                handleGoogleAuthExchange(pending.code)
            }
            is DeepLink -> {
                DeepLinkHandler.consumePendingDeepLink()?.let { deepLink ->
                    handleDeepLink(
                        deepLink = deepLink,
                        onNavigateToEvent = { eventId ->
                            eventCardReturnScreen = "home"
                            selectedEventId = eventId
                            currentScreen = "event_card"
                        },
                        onNavigateToTicket = { ticketId ->
                            setTicketNavigation(ticketId, returnScreen = "home")
                            runIfAuthenticated("ticket") {
                                currentScreen = "ticket"
                            }
                        },
                        onNavigateToUser = { userId ->
                            userCardHistory.clear()
                            selectedUserId = userId
                            userCardReturnScreen = "home"
                            currentScreen = "user_card"
                        }
                    )
                }
            }
            else -> Unit
        }

        // Then subscribe to deep link flow for subsequent deep links
        DeepLinkHandler.deepLinks.collect { deepLink ->
            when (deepLink) {
                is DeepLink.PasswordReset -> {
                    authStartScreen = AuthScreen.LOGIN
                    authReturnScreen = "home"
                    authCancelScreen = if (isGuestAllowed(currentScreen)) currentScreen else "home"
                    currentScreen = "auth"
                }
                is DeepLink.GoogleAuthExchange -> {
                    handleGoogleAuthExchange(deepLink.code)
                }
                else -> {
                    handleDeepLink(
                        deepLink = deepLink,
                        onNavigateToEvent = { eventId ->
                            eventCardReturnScreen = "home"
                            selectedEventId = eventId
                            currentScreen = "event_card"
                        },
                        onNavigateToTicket = { ticketId ->
                            setTicketNavigation(ticketId, returnScreen = "home")
                            runIfAuthenticated("ticket") {
                                currentScreen = "ticket"
                            }
                        },
                        onNavigateToUser = { userId ->
                            userCardHistory.clear()
                            selectedUserId = userId
                            userCardReturnScreen = "home"
                            currentScreen = "user_card"
                        }
                    )
                }
            }
        }
    }

    var isWizardPrefilling by remember { mutableStateOf(false) }

    val openOwnProfile: (String) -> Unit = openOwnProfile@{ origin ->
        if (!isAuthenticated) {
            pendingOwnProfileAfterAuth = true
            pendingProfileOrigin = origin
            val cancelScreen = if (isGuestAllowed(origin)) origin else "home"
            requestAuth("home", cancelScreen, preserveProfilePending = true)
            return@openOwnProfile
        }
        val userId = authRepository.getUserId()?.toString()
        if (!userId.isNullOrBlank()) {
            userCardHistory.clear()
            selectedUserId = userId
            userCardReturnScreen = origin
            currentScreen = "user_card"
        } else {
            profileReturnScreen = origin
            currentScreen = "profile"
        }
    }

    // Create HomeViewModel once at app level to prevent recreation
    val homeViewModel = remember { com.ijoin.ui.home.HomeViewModel() }
    val ticketsViewModel = remember { TicketsViewModel() }
    val handleUserCardBack: () -> Unit = {
        if (userCardHistory.isNotEmpty()) {
            val lastIndex = userCardHistory.lastIndex
            val previousUserId = userCardHistory[lastIndex]
            userCardHistory.removeAt(lastIndex)
            selectedUserId = previousUserId
        } else {
            userCardHistory.clear()
            currentScreen = userCardReturnScreen
        }
    }
    val handleTicketBack: () -> Unit = {
        if (ticketReturnScreen == "event_card") {
            val eventId = ticketReturnEventId ?: selectedEventId
            if (eventId.isNullOrBlank()) {
                currentScreen = "home"
            } else {
                selectedEventId = eventId
                currentScreen = "event_card"
            }
        } else {
            currentScreen = ticketReturnScreen
        }
    }

    // Handle system back button
    BackHandler(enabled = currentScreen != "home") {
        when {
            currentScreen == "auth" -> {
                pendingOwnProfileAfterAuth = false
                currentScreen = authCancelScreen
            }
            currentScreen == "event_card" -> currentScreen = eventCardReturnScreen
            currentScreen == "event_details" -> currentScreen = "home"
            currentScreen == "ticket" -> handleTicketBack()
            currentScreen == "tickets" -> currentScreen = "home"
            currentScreen == "payment" -> {
                paymentArgs = null
                currentScreen = "home"
            }
            currentScreen == "profile" -> currentScreen = profileReturnScreen
            currentScreen == "billing" -> {
                billingUserId = null
                currentScreen = billingReturnScreen
                billingReturnScreen = "user_card"
            }
            currentScreen == "user_card" -> handleUserCardBack()
            currentScreen == "following_list" -> {
                followingListUserId = null
                currentScreen = followingListReturnScreen
                followingListReturnScreen = "user_card"
            }
            currentScreen == "chat_conversation" -> {
                val destination = if (chatEntryPoint == ChatEntryPoint.EVENT) {
                    "event_card"
                } else {
                    "chat_list"
                }
                chatEntryPoint = ChatEntryPoint.HOME
                selectedChatThread = null
                pendingChatUserId = null
                pendingChatUserName = null
                currentScreen = destination
            }
            currentScreen == "chat_list" -> currentScreen = "home"
            currentScreen == "create_event_wizard" -> {
                if (!isSavingWizard && !isWizardPrefilling) {
                    wizardErrorMessage = null
                    val destination = wizardEditingEventId?.let { wizardReturnScreen } ?: "home"
                    wizardEditingEventId = null
                    wizardInitialData = null
                    isWizardPrefilling = false
                    currentScreen = destination
                    wizardReturnScreen = "home"
                }
            }
        }
    }

    when (currentScreen) {
            "auth" -> AuthenticationFlow(
                onAuthSuccess = {
                    onAuthSuccess()
                    val destination = authReturnScreen
                    authReturnScreen = "home"
                    authCancelScreen = "home"
                    if (pendingOwnProfileAfterAuth) {
                        pendingOwnProfileAfterAuth = false
                        val origin = pendingProfileOrigin
                        val userId = authRepository.getUserId()?.toString()
                        if (!userId.isNullOrBlank()) {
                            userCardHistory.clear()
                            selectedUserId = userId
                            userCardReturnScreen = origin
                            currentScreen = "user_card"
                        } else {
                            profileReturnScreen = origin
                            currentScreen = "profile"
                        }
                    } else {
                        currentScreen = destination
                    }
                },
                initialScreen = authStartScreen
            )
            "home" -> HomeScreen(
                onEventClick = { eventId ->
                    eventCardReturnScreen = "home"
                    selectedEventId = eventId
                    currentScreen = "event_card"
                },
                onTicketClick = { ticketId ->
                    setTicketNavigation(ticketId, returnScreen = "home")
                    runIfAuthenticated("ticket") {
                        currentScreen = "ticket"
                    }
                },
                onCreateEventClick = {
                    runIfAuthenticated("create_event_wizard") {
                        currentScreen = "create_event_wizard"
                    }
                },
                onOpenTickets = {
                    runIfAuthenticated("tickets") {
                        currentScreen = "tickets"
                    }
                },
                onOpenProfile = {
                    openOwnProfile("home")
                },
                onOpenChats = {
                    selectedChatThread = null
                    pendingChatUserId = null
                    pendingChatUserName = null
                    chatEntryPoint = ChatEntryPoint.HOME
                    runIfAuthenticated("chat_list") {
                        currentScreen = "chat_list"
                    }
                },
                isAuthenticated = isAuthenticated,
                viewModel = homeViewModel
            )
            "event_card" -> selectedEventId?.let { eventId ->
                val eventNavTab = when (eventCardReturnScreen) {
                    "tickets" -> "tickets"
                    "profile" -> "profile"
                    else -> "home"
                }
                EventScreenWrapper(
                    eventId = eventId,
                    isAuthenticated = isAuthenticated,
                    onRequireAuth = { requestAuth("event_card") },
                    onBackClick = { currentScreen = eventCardReturnScreen },
                    onBuyTicketClick = { paymentFlowArgs ->
                        if (isAuthenticated) {
                            paymentArgs = paymentFlowArgs
                            currentScreen = "payment"
                        } else {
                            requestAuth("event_card")
                        }
                    },
                    onContactOrganizerClick = { organizerId, organizerName ->
                        if (organizerId.isNotBlank()) {
                            selectedChatThread = null
                            pendingChatUserId = organizerId
                            pendingChatUserName = organizerName
                            chatEntryPoint = ChatEntryPoint.EVENT
                            runIfAuthenticated("chat_conversation") {
                                currentScreen = "chat_conversation"
                            }
                        }
                    },
                    onOpenOrganizerProfile = { organizerId ->
                        if (organizerId.isNotBlank()) {
                            userCardHistory.clear()
                            selectedUserId = organizerId
                            userCardReturnScreen = "event_card"
                            currentScreen = "user_card"
                        }
                    },
                    onStartChatWithUser = { userId, displayName ->
                        if (userId.isNotBlank()) {
                            selectedChatThread = null
                            pendingChatUserId = userId
                            pendingChatUserName = displayName
                            chatEntryPoint = ChatEntryPoint.EVENT
                            runIfAuthenticated("chat_conversation") {
                                currentScreen = "chat_conversation"
                            }
                        }
                    },
                    onEditEvent = {
                        wizardReturnScreen = "event_card"
                        wizardEditingEventId = eventId
                        wizardInitialData = null
                        wizardErrorMessage = null
                        isWizardPrefilling = true
                        currentScreen = "create_event_wizard"
                        scope.launch {
                            eventRepository.getEventById(eventId)
                                .onSuccess { fetched ->
                                    wizardInitialData = fetched.toWizardData()
                                }
                                .onFailure { error ->
                                    wizardErrorMessage = error.message
                                }
                            isWizardPrefilling = false
                        }
                    },
                    onNavigateToTicket = { ticketId ->
                        setTicketNavigation(
                            ticketId = ticketId.toString(),
                            returnScreen = "event_card",
                            returnEventId = eventId
                        )
                        runIfAuthenticated("ticket") {
                            currentScreen = "ticket"
                        }
                    },
                    onOpenHome = { currentScreen = "home" },
                    onOpenExplore = { /* TODO: implement explore screen */ },
                    onCreateEventClick = {
                        runIfAuthenticated("create_event_wizard") {
                            currentScreen = "create_event_wizard"
                        }
                    },
                    onOpenTickets = {
                        runIfAuthenticated("tickets") {
                            currentScreen = "tickets"
                        }
                    },
                    onOpenProfile = { openOwnProfile("event_card") },
                    selectedTab = eventNavTab
                )
            }
            "user_card" -> selectedUserId?.let { userId ->
                UserCardScreen(
                    userId = userId,
                    onBackClick = handleUserCardBack,
                    onOpenEvent = { eventId ->
                        eventCardReturnScreen = "user_card"
                        selectedEventId = eventId
                        currentScreen = "event_card"
                    },
                    onEditProfile = {
                        val selfId = authRepository.getUserId()?.toString()
                        if (selfId != null && selfId == userId) {
                            profileReturnScreen = "user_card"
                            currentScreen = "profile"
                        }
                    },
                    onEditEvent = { event ->
                        wizardReturnScreen = "user_card"
                        wizardEditingEventId = event.id
                        wizardInitialData = null
                        wizardErrorMessage = null
                        isWizardPrefilling = true
                        currentScreen = "create_event_wizard"
                        scope.launch {
                            eventRepository.getEventById(event.id)
                                .onSuccess { fetched ->
                                    wizardInitialData = fetched.toWizardData()
                                }
                                .onFailure { error ->
                                    wizardErrorMessage = error.message
                                }
                            isWizardPrefilling = false
                        }
                    },
                    onOpenFollowingList = { targetUserId ->
                        if (targetUserId.isNotBlank()) {
                            followingListUserId = targetUserId
                            followingListReturnScreen = "user_card"
                            currentScreen = "following_list"
                        }
                    },
                    onOpenUserProfile = { targetUserId ->
                        if (targetUserId.isNotBlank() && targetUserId != userId) {
                            userCardHistory.add(userId)
                            selectedUserId = targetUserId
                        }
                    },
                    onOpenBilling = { ownerId ->
                        billingReturnScreen = currentScreen
                        billingUserId = ownerId
                        currentScreen = "billing"
                    },
                    onOpenHome = { currentScreen = "home" },
                    onOpenExplore = { /* TODO: implement explore screen */ },
                    onCreateEventClick = {
                        runIfAuthenticated("create_event_wizard") {
                            currentScreen = "create_event_wizard"
                        }
                    },
                    onOpenTickets = {
                        runIfAuthenticated("tickets") {
                            currentScreen = "tickets"
                        }
                    },
                    onOpenProfile = { openOwnProfile("user_card") }
                )
            }
            "billing" -> billingUserId?.let { ownerId ->
                BillingScreen(
                    userId = ownerId,
                    onBackClick = {
                        billingUserId = null
                        currentScreen = billingReturnScreen
                        billingReturnScreen = "user_card"
                    },
                    onOpenHome = { currentScreen = "home" },
                    onOpenExplore = { /* TODO: implement explore screen */ },
                    onCreateEventClick = {
                        runIfAuthenticated("create_event_wizard") {
                            currentScreen = "create_event_wizard"
                        }
                    },
                    onOpenTickets = {
                        runIfAuthenticated("tickets") {
                            currentScreen = "tickets"
                        }
                    },
                    onOpenProfile = { openOwnProfile("billing") },
                    onOpenUrl = { url -> openUrl(url) }
                )
            }
            "following_list" -> followingListUserId?.let { listOwnerId ->
                FollowingListScreen(
                    userId = listOwnerId,
                    onUserSelected = { targetUserId ->
                        if (targetUserId.isNotBlank()) {
                            userCardHistory.clear()
                            selectedUserId = targetUserId
                            userCardReturnScreen = "following_list"
                            currentScreen = "user_card"
                        }
                    },
                    onBackClick = {
                        followingListUserId = null
                        currentScreen = followingListReturnScreen
                        followingListReturnScreen = "user_card"
                    },
                    onOpenHome = { currentScreen = "home" },
                    onOpenExplore = { /* TODO: implement explore screen */ },
                    onCreateEventClick = {
                        runIfAuthenticated("create_event_wizard") {
                            currentScreen = "create_event_wizard"
                        }
                    },
                    onOpenTickets = {
                        runIfAuthenticated("tickets") {
                            currentScreen = "tickets"
                        }
                    },
                    onOpenProfile = { openOwnProfile("following_list") }
                )
            }
            "event_details" -> selectedEventId?.let { eventId ->
                EventDetailsScreen(
                    eventId = eventId,
                    onBackClick = { currentScreen = "home" },
                    onBuyTicketClick = { /* TODO */ }
                )
            }
            "ticket" -> selectedTicketId?.let { ticketId ->
                TicketScreen(
                    ticketId = ticketId,
                    onBackClick = handleTicketBack,
                    onEventClick = { eventId ->
                        if (eventId.isNotBlank()) {
                            eventCardReturnScreen = "ticket"
                            selectedEventId = eventId
                            currentScreen = "event_card"
                        }
                    },
                    onOpenHome = { currentScreen = "home" },
                    onOpenExplore = { /* TODO: implement explore screen */ },
                    onCreateEventClick = {
                        runIfAuthenticated("create_event_wizard") {
                            currentScreen = "create_event_wizard"
                        }
                    },
                    onOpenTickets = { currentScreen = "tickets" },
                    onOpenProfile = { openOwnProfile("ticket") }
                )
            }
            "tickets" -> TicketsScreen(
                onTicketClick = { ticketId ->
                    setTicketNavigation(ticketId, returnScreen = "tickets")
                    currentScreen = "ticket"
                },
                onOpenHome = { currentScreen = "home" },
                onOpenExplore = { /* TODO: implement explore screen */ },
                onCreateEventClick = {
                    runIfAuthenticated("create_event_wizard") {
                        currentScreen = "create_event_wizard"
                    }
                },
                onOpenTickets = { currentScreen = "tickets" },
                onOpenProfile = { openOwnProfile("tickets") },
                modifier = Modifier.fillMaxSize(),
                viewModel = ticketsViewModel
            )
            "payment" -> paymentArgs?.let { args ->
                PaymentScreen(
                    args = args,
                    onBackClick = {
                        paymentArgs = null
                        currentScreen = "home"
                    },
                    onTicketReady = { ticketId ->
                        setTicketNavigation(
                            ticketId = ticketId,
                            returnScreen = "event_card",
                            returnEventId = args.eventId
                        )
                        paymentArgs = null
                        currentScreen = "ticket"
                    }
                )
            }
            "profile" -> ProfileScreen(
                authRepository = authRepository,
                onBackClick = { currentScreen = profileReturnScreen },
                onLogout = {
                    scope.launch {
                        authRepository.logout()
                        onLogout()
                        currentScreen = "home"
                    }
                },
                onOpenHome = { currentScreen = "home" },
                onOpenExplore = { /* TODO: implement explore screen */ },
                onCreateEventClick = {
                    runIfAuthenticated("create_event_wizard") {
                        currentScreen = "create_event_wizard"
                    }
                },
                onOpenTickets = { currentScreen = "tickets" },
                onOpenProfile = { openOwnProfile("profile") }
            )
            "chat_list" -> ChatListScreen(
                onBackClick = {
                    currentScreen = "home"
                },
                onChatSelected = { chat ->
                    val participantId = chat.otherUserId
                    if (chat.group || !participantId.isNullOrBlank()) {
                        selectedChatThread = chat
                        pendingChatUserId = participantId
                        pendingChatUserName = if (chat.group) {
                            chat.title
                        } else {
                            chat.otherUserName
                        }
                        chatEntryPoint = ChatEntryPoint.LIST
                        currentScreen = "chat_conversation"
                    }
                },
                onStartChatWithUser = { userId, displayName ->
                    if (userId.isNotBlank()) {
                        selectedChatThread = null
                        pendingChatUserId = userId
                        pendingChatUserName = displayName
                        chatEntryPoint = ChatEntryPoint.LIST
                        currentScreen = "chat_conversation"
                    }
                },
                onOpenHome = { currentScreen = "home" },
                onOpenExplore = { /* TODO: implement explore screen */ },
                onCreateEventClick = {
                    runIfAuthenticated("create_event_wizard") {
                        currentScreen = "create_event_wizard"
                    }
                },
                onOpenTickets = {
                    runIfAuthenticated("tickets") {
                        currentScreen = "tickets"
                    }
                },
                onOpenProfile = { openOwnProfile("chat_list") }
            )
            "chat_conversation" -> {
                val userId = pendingChatUserId
                val thread = selectedChatThread
                if (thread != null || userId != null) {
                    ChatScreen(
                        initialThread = thread,
                        otherUserId = userId,
                        otherUserName = pendingChatUserName ?: thread?.title,
                        onBackClick = {
                            if (chatEntryPoint == ChatEntryPoint.EVENT) {
                                chatEntryPoint = ChatEntryPoint.HOME
                                currentScreen = "event_card"
                            } else {
                                chatEntryPoint = ChatEntryPoint.HOME
                                currentScreen = "chat_list"
                            }
                            selectedChatThread = null
                            pendingChatUserId = null
                            pendingChatUserName = null
                        },
                        onOpenHome = { currentScreen = "home" },
                        onOpenExplore = { /* TODO: implement explore screen */ },
                        onCreateEventClick = {
                            runIfAuthenticated("create_event_wizard") {
                                currentScreen = "create_event_wizard"
                            }
                        },
                        onOpenTickets = {
                            runIfAuthenticated("tickets") {
                                currentScreen = "tickets"
                            }
                        },
                        onOpenProfile = { openOwnProfile("chat_conversation") }
                    )
                } else {
                    LaunchedEffect(Unit) {
                        currentScreen = "home"
                    }
                }
            }
            "create_event_wizard" -> {
                CreateEventWizardScreen(
                    eventId = wizardEditingEventId,
                    initialData = wizardInitialData,
                    isPrefilling = isWizardPrefilling,
                    defaultCurrencyCode = userCurrency,
                    onBackClick = {
                        if (!isSavingWizard && !isWizardPrefilling) {
                            wizardErrorMessage = null
                            val destination = wizardEditingEventId?.let { wizardReturnScreen } ?: "home"
                            wizardEditingEventId = null
                            wizardInitialData = null
                            isWizardPrefilling = false
                            currentScreen = destination
                            wizardReturnScreen = "home"
                        }
                    },
                    onSaveDraft = { eventData ->
                        if (isSavingWizard) return@CreateEventWizardScreen
                        scope.launch {
                            isSavingWizard = true
                            wizardErrorMessage = null
                            eventRepository.createEventFromWizard(eventData.toRequest())
                                .onFailure { error ->
                                    wizardErrorMessage = error.message
                                }
                            isSavingWizard = false
                        }
                    },
                    onCreateEvent = { eventData ->
                        if (isSavingWizard) return@CreateEventWizardScreen
                        scope.launch {
                            isSavingWizard = true
                            wizardErrorMessage = null
                            eventRepository.createEventFromWizard(eventData.toRequest())
                                .onSuccess { createdEvent ->
                                    wizardEditingEventId = null
                                    wizardInitialData = null
                                    wizardReturnScreen = "home"
                                    eventCardReturnScreen = "home"
                                    selectedEventId = createdEvent.id
                                    currentScreen = "event_card"
                                }
                                .onFailure { error ->
                                    wizardErrorMessage = error.message
                                }
                            isSavingWizard = false
                        }
                    },
                    onUpdateEvent = { editingId, eventData ->
                        if (isSavingWizard) return@CreateEventWizardScreen
                        scope.launch {
                            isSavingWizard = true
                            wizardErrorMessage = null
                            eventRepository.updateEventFromWizard(editingId, eventData.toRequest())
                                .onSuccess {
                                    wizardEditingEventId = null
                                    wizardInitialData = null
                                    val destination = wizardReturnScreen
                                    wizardReturnScreen = "home"
                                    currentScreen = destination
                                }
                                .onFailure { error ->
                                    wizardErrorMessage = error.message
                                }
                            isSavingWizard = false
                        }
                    }
                )

                if (wizardErrorMessage != null) {
                    AlertDialog(
                        onDismissRequest = { wizardErrorMessage = null },
                        title = { Text(strings.eventSaveErrorTitle) },
                        text = {
                            Column {
                                Text(strings.eventSaveErrorMessage)
                                wizardErrorMessage?.takeIf { it.isNotBlank() }?.let { details ->
                                    Spacer(modifier = Modifier.height(8.dp))
                                    Text(
                                        details,
                                        style = MaterialTheme.typography.bodySmall,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                }
                            }
                        },
                        confirmButton = {
                            TextButton(onClick = { wizardErrorMessage = null }) {
                                Text(strings.ok)
                            }
                        }
                    )
                }
            }
        }
    }

private fun handleDeepLink(
    deepLink: DeepLink,
    onNavigateToEvent: (String) -> Unit,
    onNavigateToTicket: (String) -> Unit,
    onNavigateToUser: (String) -> Unit
) {
    when (deepLink) {
        is DeepLink.Event -> onNavigateToEvent(deepLink.eventId)
        is DeepLink.Ticket -> onNavigateToTicket(deepLink.ticketId)
        is DeepLink.User -> onNavigateToUser(deepLink.userId)
        is DeepLink.PasswordReset -> Unit
        is DeepLink.GoogleAuthExchange -> Unit
        DeepLink.Home -> { /* stay on home */ }
    }
}
